import { useState } from "react";
import { createOffer } from "../../../api/offers";
import { OfferCreate, CallType } from "../../../models/offers";
import CoverageSelector from "../OfferDetails/Admin/CoverageSelector";
import OfferSection from "../OfferDetails/OfferDetailsSection";
import SchedulePicker from "../OfferDetails/Admin/ScheduleInput";
import toast from "react-hot-toast";
import { uploadZipList } from "../../../api/fileSystem";
import NegativeKeywordSection from "../OfferDetails/Section/NegativeKeywordSection";
const trafficOptions = [
    "Paid Search", "SEO", "Social Media", "Display Ads", "Print", "Radio/TV",
    "Directory Assistance", "Free 411", "Carrier Intercept", "Mis-Dials",
    "1800 Numbers", "Direct Mail", "SMS", "Email Marketing", "Outbound Transfer",
    "Facebook Marketplace", "GMB", "Other"
];

export default function OfferCreateForm({ onClose, onCreated, isUser }: { onClose: () => void, onCreated: (offer: any) => void; isUser: boolean }) {
    const [offer, setOffer] = useState<OfferCreate>({
        vertical: "",
        coverage: { state_list: [], number_of_zip_codes: 0 },
        category: "",
        payout_type: "fixed",
        description: "",
        payout: 0,
        campaign_type: "",
        connected_duration: null,
        call_type: null,
        schedule: { days: [], hours: [] },
        is_supply: isUser,

        conversion_type: "CPL",
        languages: [],
        caps_amount: 0,
        caps_type: "",
        traffic_allowed: [],
        restrictions: `Creative Approval:
· All creatives, landing pages, and ad copy must be approved
· Traffic must come from owned and operated sites or approved media campaigns
· Unapproved materials, including those from sub-publishers, may lead to nonpayment and suspension

Advertising and Copy:
· No branded bidding, logos, or trademarks (see negative keyword list)
· No guaranteed discounts or dollar amounts in savings claims
· No use of "government programs" or "state savings programs" or similar misleading ad copy

Traffic Sources:
· DIRECT - CONSUMER INITIATED INBOUND CALLS ONLY
· No call transfers, including inbound calls routing through call centers
· No traffic from: SMS, incentivized sources, co-registration, email blasts, display ads, direct mail, or misdials`, recording_permitted: false,
        visible: true,
        live: true,

        creative_assets: null,
        target_profile: null,
        negative_keyword_list: [],
        ivr_type: null,
        ivr_details: null,
        approved_ad_copy: null,
    });
    const [zipListFile, setZipListFile] = useState<File | null>(null);

    const handleChange = <K extends keyof OfferCreate>(field: K, value: OfferCreate[K]) => {
        setOffer((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {

        const hasStates =
            offer.coverage?.state_list &&
            offer.coverage.state_list.length > 0;

        const hasZips =
            (offer.coverage?.number_of_zip_codes ?? 0) > 0;

        const isUploadingZipFile = !!zipListFile;


        if (!offer.vertical?.trim()) {
            toast.error("Vertical is required");
            return;
        }

        if (!offer.coverage) {
            toast.error("Coverage is required");
            return;
        }

        if (!offer.description?.trim()) {
            toast.error("Description is required");
            return;
        }

        if (!offer.category?.trim()) {
            toast.error("Category is required");
            return;
        }

        if (!offer.campaign_type) {
            toast.error("Campaign Type is required");
            return;
        }

        if (
            offer.payout_type === "fixed" &&
            (!offer.payout || offer.payout <= 0)
        ) {
            toast.error("Payout must be greater than 0");
            return;
        }

        if (!offer.conversion_type?.trim()) {
            toast.error("Conversion Type is required");
            return;
        }
        if (!hasStates && !hasZips && !isUploadingZipFile) {
            toast.error("Coverage is required.");
            return;
        }
        if (!Array.isArray(offer.schedule?.days) || offer.schedule?.days.length === 0) {
            toast.error("Schedule must include at least one day");
            return;
        }

        if (!offer.languages || offer.languages.length === 0) {
            toast.error("At least one language is required");
            return;
        }

        if ((offer.caps_amount === undefined || offer.caps_amount === null) && !isUser) {
            toast.error("Caps amount is required");
            return;
        }

        // if ((!offer.caps_type?.trim()) && !isUser) {
        //     toast.error("Caps type is required");
        //     return;
        // }

        if (!offer.traffic_allowed || offer.traffic_allowed.length === 0) {
            toast.error("At least one allowed traffic type is required");
            return;
        }

        if (!offer.restrictions?.trim()) {
            toast.error("Restrictions are required");
            return;
        }

        if (offer.recording_permitted === undefined || offer.recording_permitted === null) {
            toast.error("Recording permission must be specified");
            return;
        }

        if ((offer.live === undefined || offer.live === null) && !isUser) {
            toast.error("Live flag must be specified");
            return;
        }

        if ((offer.visible === undefined || offer.visible === null) && !isUser) {
            toast.error("Visibility flag must be specified");
            return;
        }

        if (offer.campaign_type === "Calls") {
            if (offer.conversion_type === "Duration") {
                if (!offer.call_type?.flex) {
                    toast.error("Call type is required for Duration calls");
                    return;
                }
                if (offer.call_type.flex === "fixed" && !offer.connected_duration) {
                    toast.error("Connected duration is required for fixed Duration calls");
                    return;
                }
                if (!offer.call_type?.on_type) {
                    toast.error("Criteria is required for Duration calls");
                    return;
                }
            }
        }


        try {
            const payload: OfferCreate = {
                ...offer,
                is_supply: isUser,
            };

            if (
                payload.campaign_type === "Leads" || payload.campaign_type === "Traffic"
            ) {
                payload.call_type = null;
                payload.connected_duration = null;
            }

            if (payload.payout_type === "variable") {
                // payload type may not include payout for variable
                delete (payload as Partial<OfferCreate>).payout;
            }
            if (isUser) {
                payload.caps_amount = 0
                payload.caps_type = "Testing Cap"
                payload.visible = false;
                payload.live = false;
            }

            const created = await createOffer(payload);

            if ((offer.coverage?.number_of_zip_codes ?? 0) > 0 && zipListFile) {
                await uploadZipList(zipListFile, created.id);
            }
            if (created.is_supply) {
                toast.success("Supply created successfully");

            }
            else {
                toast.success("Offer created successfully");
            }
            onCreated(created);
            onClose();
        } catch (err) {
            console.error("Error creating offer:", err);
            toast.error("Failed to create offer");
        }
    };

    const showCall = ["Calls"].includes(offer.campaign_type);
    const showDuration = showCall && offer.call_type?.flex === "fixed";

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div className="bg-white rounded-2xl shadow-xl w-[1000px] max-w-[95vw] max-h-[90vh] overflow-y-auto p-8 relative">
                {/* X button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 font-bold text-lg"
                >
                    ✕
                </button>

                <h2 className="text-2xl font-bold mb-6">  {isUser ? "Create Supply" : "Create Offer"}</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Header-ish fields (Vertical, Category, Campaign Type, Live & Visible) */}
                    <OfferSection label="Vertical">
                        <select
                            className="border p-2 rounded w-full"
                            value={offer.vertical}
                            onChange={(e) => handleChange("vertical", e.target.value)}
                        >
                            <option value="">Select a vertical</option>
                            <option value="Insurance">Insurance</option>
                            <option value="Home Services">Home Services</option>
                            <option value="Legal">Legal</option>
                            <option value="Debt">Debt</option>
                            <option value="Travel">Travel</option>
                            <option value="Other">Other</option>
                        </select>
                    </OfferSection>

                    <OfferSection label="Category">
                        <input
                            type="text"
                            className="border p-2 rounded w-full"
                            value={offer.category}
                            onChange={(e) => handleChange("category", e.target.value)}
                        />
                    </OfferSection>
                    {!isUser && (
                        <>
                            <OfferSection label="Live">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={offer.live}
                                        onChange={(e) => handleChange("live", e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">{offer.live ? "Live" : "Offline"}</span>
                                </div>
                            </OfferSection>

                            <OfferSection label="Visibility">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={offer.visible}
                                        onChange={(e) => handleChange("visible", e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">
                                        {offer.visible ? "Visible to users" : "Hidden from users"}
                                    </span>
                                </div>
                            </OfferSection>
                        </>
                    )
                    }
                    <OfferSection label="Payout">
                        <div className="flex flex-col gap-2">
                            <select
                                className="border p-2 rounded"
                                value={offer.payout_type}
                                onChange={(e) => handleChange("payout_type", e.target.value)}
                            >
                                <option value="fixed">Fixed</option>
                                <option value="variable">Variable</option>
                            </select>

                            {offer.payout_type === "fixed" && (
                                <input
                                    type="number"
                                    className="border p-2 rounded"
                                    placeholder="Payout"
                                    value={offer.payout ?? ""}
                                    onChange={(e) =>
                                        handleChange(
                                            "payout",
                                            e.target.value === "" ? undefined : Number(e.target.value)
                                        )
                                    }
                                />
                            )}
                        </div>
                    </OfferSection>
                    <OfferSection label="Campaign Type">
                        <select
                            className="border p-2 rounded w-full"
                            value={offer.campaign_type}
                            onChange={(e) => handleChange("campaign_type", e.target.value)}
                        >
                            <option value="" disabled>
                                Select campaign type
                            </option>
                            <option value="Calls">Calls</option>
                            <option value="Leads">Leads</option>
                            <option value="Traffic">Traffic</option>
                        </select>
                    </OfferSection>





                    <OfferSection label="Conversion">
                        <div className="flex flex-col gap-2">
                            <select
                                className="border p-2 rounded w-full"
                                value={offer.conversion_type ?? ""}
                                onChange={(e) => handleChange("conversion_type", e.target.value)}
                            >
                                <option value="">Select Conversion Type</option>
                                <option value="CPA">CPA</option>
                                <option value="CPL">CPL</option>
                                {offer.campaign_type === "Calls" && <option value="Duration">Duration</option>}
                            </select>

                            {offer.campaign_type === "Calls" && offer.conversion_type === "Duration" && (
                                <>
                                    <select
                                        className="border p-2 rounded"
                                        value={offer.call_type?.flex ?? ""}
                                        onChange={(e) => {
                                            const newFlex = e.target.value as "fixed" | "variable";
                                            handleChange("call_type", {
                                                ...(offer.call_type ?? ({} as CallType)),
                                                flex: newFlex,
                                            });
                                            if (newFlex !== "fixed") handleChange("connected_duration", null);
                                        }}
                                    >
                                        <option value="">Select Duration Type</option>
                                        <option value="fixed">Fixed</option>
                                        <option value="variable">Variable</option>
                                    </select>

                                    {offer.call_type?.flex === "fixed" && (
                                        <input
                                            type="number"
                                            className="border p-2 rounded"
                                            placeholder="Connected Duration (seconds)"
                                            value={offer.connected_duration ?? ""}
                                            onChange={(e) =>
                                                handleChange(
                                                    "connected_duration",
                                                    e.target.value === "" ? undefined : Number(e.target.value)
                                                )
                                            }
                                        />
                                    )}

                                    <select
                                        className="border p-2 rounded"
                                        value={offer.call_type?.on_type ?? ""}
                                        onChange={(e) =>
                                            handleChange("call_type", {
                                                ...(offer.call_type ?? ({} as CallType)),
                                                on_type: e.target.value,
                                            })
                                        }
                                    >
                                        <option value="">Select Criteria</option>
                                        <option value="Connected Call Duration">Connected Call Duration</option>
                                        <option value="Total Call Duration">Total Call Duration</option>
                                    </select>
                                </>
                            )}
                        </div>
                    </OfferSection>

                    {/* Coverage */}
                    <OfferSection label="Coverage">
                        <CoverageSelector editedOffer={offer} handleChange={handleChange} setZipListFile={setZipListFile} />
                    </OfferSection>

                    {/* Schedule */}
                    <OfferSection label="Schedule">
                        <SchedulePicker
                            schedule={offer.schedule}
                            onChange={(newSchedule) => handleChange("schedule", newSchedule)}
                        />
                    </OfferSection>

                    {/* Languages */}
                    <OfferSection label="Languages">
                        <div className="space-y-1">
                            {["English", "Spanish"].map((lang) => (
                                <label key={lang} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={offer.languages?.includes(lang) || false}
                                        onChange={(e) => {
                                            const current = offer.languages ?? []
                                            const updated = e.target.checked
                                                ? [...current, lang]
                                                : current.filter((l) => l !== lang)
                                            handleChange("languages", updated)
                                        }}
                                    />
                                    <span>{lang}</span>
                                </label>
                            ))}
                        </div>
                    </OfferSection>

                    {/* Caps */}
                    {!isUser && (

                        <OfferSection label="Caps">
                            <div className="flex flex-row gap-2 w-full">
                                <input
                                    type="number"
                                    className="border p-2 rounded w-full"
                                    placeholder="Amount"
                                    value={offer.caps_amount ?? ""}
                                    onChange={(e) => handleChange(
                                        "caps_amount",
                                        e.target.value === "" ? NaN : Number(e.target.value)
                                    )}
                                />
                                <select
                                    className="border p-2 rounded w-full"
                                    value={offer.caps_type ?? ""}
                                    onChange={(e) => handleChange("caps_type", e.target.value)}
                                >
                                    <option value="">Select type</option>
                                    <option value="Daily">Daily</option>
                                    <option value="Weekly">Weekly</option>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Globally">Globally</option>
                                    <option value="Testing Cap">Testing Cap</option>
                                </select>
                            </div>
                        </OfferSection>
                    )}

                    {/* Description */}
                    <OfferSection label="Description">
                        <textarea
                            className="border p-2 rounded w-full h-40 resize-y"
                            value={offer.description ?? ""}
                            onChange={(e) => handleChange("description", e.target.value)}
                        />
                    </OfferSection>
                    <OfferSection label="Traffic Allowed" className="sm:col-span-2">
                        {/* Select All */}
                        <div className="mb-2">
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={
                                        offer.traffic_allowed?.length === trafficOptions.length
                                    }
                                    onChange={(e) => {
                                        handleChange(
                                            "traffic_allowed",
                                            e.target.checked ? [...trafficOptions] : []
                                        );
                                    }}
                                />
                                <span>Select All</span>
                            </label>
                        </div>

                        {/* Individual checkboxes */}
                        <div className="grid grid-cols-3 gap-2">
                            {trafficOptions.map((opt) => (
                                <label key={opt} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={offer.traffic_allowed?.includes(opt) || false}
                                        onChange={(e) => {
                                            const current = offer.traffic_allowed ?? [];
                                            const updated = e.target.checked
                                                ? [...current, opt]
                                                : current.filter((t) => t !== opt);
                                            handleChange("traffic_allowed", updated);
                                        }}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                        </div>
                    </OfferSection>


                    {/* Restrictions */}
                    <OfferSection label="Restrictions" className="sm:col-span-2">
                        <textarea
                            className="border p-2 rounded w-full h-40 resize-y"
                            value={offer.restrictions ?? ""}
                            onChange={(e) => handleChange("restrictions", e.target.value)}
                        />
                    </OfferSection>

                    {/* Creative Assets */}
                    <OfferSection label="Creative Assets" show>
                        <textarea
                            className="border p-2 rounded w-full h-32 resize-y"
                            value={offer.creative_assets ?? ""}
                            onChange={(e) => handleChange("creative_assets", e.target.value)}
                        />
                    </OfferSection>

                    {/* Target Profile */}
                    <OfferSection label="Target Profile" show>
                        <textarea
                            className="border p-2 rounded w-full h-32 resize-y"
                            value={offer.target_profile ?? ""}
                            onChange={(e) => handleChange("target_profile", e.target.value)}
                        />
                    </OfferSection>

                    {/* Negative Keywords */}
                    <OfferSection label="Negative Keywords" show>
                        <NegativeKeywordSection
                            keywords={offer?.negative_keyword_list ?? []}
                            editMode={true}
                            isEditable={true}
                            onChange={(newList: string[]) => handleChange("negative_keyword_list", newList)}
                        />
                    </OfferSection>

                    {/* IVR Type */}
                    <OfferSection label="IVR Type" show>
                        <textarea
                            className="border p-2 rounded w-full h-24 resize-y"
                            value={offer.ivr_type ?? ""}
                            onChange={(e) => handleChange("ivr_type", e.target.value)}
                        />
                    </OfferSection>

                    {/* IVR Details */}
                    <OfferSection label="IVR Details" show>
                        <textarea
                            className="border p-2 rounded w-full h-24 resize-y"
                            value={offer.ivr_details ?? ""}
                            onChange={(e) => handleChange("ivr_details", e.target.value)}
                        />
                    </OfferSection>

                    {/* Approved Ad Copy */}
                    <OfferSection label="Approved Ad Copy" show>
                        <textarea
                            className="border p-2 rounded w-full h-24 resize-y"
                            value={offer.approved_ad_copy ?? ""}
                            onChange={(e) => handleChange("approved_ad_copy", e.target.value)}
                        />
                    </OfferSection>

                    {/* Recording Permitted */}
                    <OfferSection label="Recording Permitted">
                        <select
                            className="border p-2 rounded w-full"
                            value={offer.recording_permitted ? "yes" : "no"}
                            onChange={(e) =>
                                handleChange("recording_permitted", e.target.value === "yes")
                            }
                        >
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </OfferSection>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-8">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700"
                    >
                        {isUser ? "Create Supply" : "Create Offer"}
                    </button>
                </div>
            </div>
        </div >
    );
}

